import React, { useState, useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, IconButton, Menu, MenuItem, TextField, Tooltip, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import folder from './assets/open-folder.png';
import cards from './assets/flash-card.png';
import { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext'; 
import axios from 'axios';
import FolderIcon from '@mui/icons-material/Folder';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import Collapse from '@mui/material/Collapse';
import FolderOpen from '@mui/icons-material/FolderOpen';

const ItemTypes = {
  NODE: 'node',
};

const TreeNode = ({
  node,
  moveNode,
  onSelect,
  selectedFolderId,
  fetchTreeData,
  myPermissions = {
    can_create_folder: false,
    can_create_sub: false,
    can_update_folder: false,
    can_delete_folder: false,
    can_read_folder: false,
    is_admin: false,
  },
   setActivePage,
    expandedFolders = [],
  setExpandedFolders
}) => {
   const isExpanded = expandedFolders.includes(node.id);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [creatingSubfolder, setCreatingSubfolder] = useState(false);
  const [newName, setNewName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(node.name);
  const [plusAnchorEl, setPlusAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isDuplicateSubfolderAlertOpen, setIsDuplicateSubfolderAlertOpen] = useState(false);
  const [duplicateSubfolderErrorMessage, setDuplicateSubfolderErrorMessage] = useState('');
 
  const navigate = useNavigate();
  const folderFormRef = useRef(null);
  const subfolderFormRef = useRef(null);
  const { darkMode } = useContext(ThemeContext);
  const { mode, customColors } = useContext(ThemeContext);
  
  const apiUrl = process.env.REACT_APP_BASE_URL;
const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.NODE,
    item: { id: node.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const [, drop] = useDrop({
    accept: ItemTypes.NODE,
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        moveNode(item.id, node.id);
      }
    },
  });

  const hasChildren = node.children?.length > 0;

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handlePlusClick = (event) => {
    event.stopPropagation();
    setPlusAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setPlusAnchorEl(null);
  };



  const handleCreateClick = (type) => {
    if (node.type === 'subfolder') {
      setSnackbar({
        open: true,
        message: "Cannot create folders inside a subfolder.",
        severity: "warning",
      });
      return;
    }
    if (type === 'folder') {
      if (myPermissions.can_create_folder || myPermissions.is_admin) {
        setCreatingFolder(true);
        setCreatingSubfolder(false);
      } else {
        setSnackbar({
          open: true,
          message: "You do not have permission to create folder.",
          severity: "warning",
        });
      }
    } else if (type === 'subfolder') {
      if ((myPermissions.can_create_folder && myPermissions.can_create_sub) || myPermissions.is_admin) {
        setCreatingSubfolder(true);
        setCreatingFolder(false);
      } else {
        setSnackbar({
          open: true,
          message: myPermissions.can_create_folder
            ? "You do not have permission to create subjective questions."
            : "You do not have permission to create folder or subjective questions.",
          severity: "warning",
        });
      }
    }
    handleMenuClose();
  };

const handleCreateSubmit = (e, type) => {
    e.preventDefault();
    if (newName.trim() === '') {
      setSnackbar({
        open: true,
        message: 'Error: Folder name cannot be empty',
        severity: "error",
      });
      return;
    }
    if (type === 'folder' && !(myPermissions.can_create_folder || myPermissions.is_admin)) {
      setSnackbar({
        open: true,
        message: "You do not have permission to create folder.",
        severity: "warning",
      });
      return;
    }
    if (type === 'subfolder' && (!(myPermissions.can_create_folder && myPermissions.can_create_sub) && !myPermissions.is_admin)) {
      setSnackbar({
        open: true,
        message: myPermissions.can_create_folder
          ? "You do not have permission to create subjective questions."
          : "You do not have permission to create folder or subjective questions.",
        severity: "warning",
      });
      return;
    }

    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem('authToken');
    const url =
      type === 'folder'
        ? `${apiUrl}/folders/${node.id}/create_folder/`
        : `${apiUrl}/folders/${node.id}/create_subfolder/`;

    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: newName }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json().then((newFolder) => {
            setNewName('');
            setCreatingFolder(false);
            setCreatingSubfolder(false);
            setSnackbar({
              open: true,
              message: `Successfully created ${type === 'folder' ? 'folder' : 'subfolder'}: ${newName}`,
              severity: "success",
            });
            fetchTreeData();
            if (newFolder.id) {
              setTimeout(() => {
                if (type === 'subfolder') {
                  navigate(`/cards/${newFolder.id}`);
                } else if (!node.children?.length) {
                  navigate(`/cards/${newFolder.id}`);
                }
              }, 6000);
            }
          });
        } else {
          return response.text().then((rawResponseText) => {
            let errorMsg = `Failed to create ${type === 'folder' ? 'folder' : 'subfolder'}`;
            let errorData = {};
            try {
              errorData = JSON.parse(rawResponseText);
              console.log('handleCreateSubmit error response:', errorData);
            } catch (jsonError) {
              console.error('Error parsing handleCreateSubmit JSON:', jsonError, { rawResponseText });
            }

            if (response.status === 400 && type === 'subfolder' && errorData.error && errorData.error.toLowerCase().includes('already exists')) {
              setDuplicateSubfolderErrorMessage(errorData.error);
              setIsDuplicateSubfolderAlertOpen(true);
              return;
            } else if (response.status === 400 && errorData.error) {
              errorMsg = errorData.error;
            } else if (response.status === 401) {
              errorMsg = 'Unauthorized: Please log in again';
            } else if (response.status === 403) {
              errorMsg = `Forbidden: You lack permission to create ${type === 'folder' ? 'folders' : 'subfolders'}`;
            } else if (response.status >= 500) {
              errorMsg = 'Server error: Please try again later';
            }

            setSnackbar({
              open: true,
              message: errorMsg,
              severity: "error",
            });
            throw new Error(errorMsg);
          });
        }
      })
      .catch((error) => {
        console.error('Error creating folder:', error);
        setSnackbar({
          open: true,
          message: 'Network error: Unable to connect to the server',
          severity: "error",
        });
      });
  };

  const handleCloseDuplicateSubfolderAlert = () => {
    setIsDuplicateSubfolderAlertOpen(false);
    setDuplicateSubfolderErrorMessage('');
  };

  
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

const handleEdit = () => {
    if (myPermissions.can_update_folder || myPermissions.is_admin) {
      setIsEditing(true);
      handleMenuClose();
    } else {
      setSnackbar({
        open: true,
        message: `You do not have permission to rename ${node.type === 'folder' ? 'folders' : 'subfolders'}.`,
        severity: "warning",
      });
    }
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (editedName.trim() === '') {
      setSnackbar({
        open: true,
        message: 'Error: Folder name cannot be empty',
        severity: "error",
      });
      return;
    }

    if (!(myPermissions.can_update_folder || myPermissions.is_admin)) {
      setSnackbar({
        open: true,
        message: `You do not have permission to rename ${node.type === 'folder' ? 'folders' : 'subfolders'}.`,
        severity: "warning",
      });
      return;
    }

    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem('authToken');
    const url = `${apiUrl}/folders/${node.id}/`;

    fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: editedName }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json().then(() => {
            setIsEditing(false);
            setSnackbar({
              open: true,
              message: `Successfully renamed ${node.type === 'folder' ? 'folder' : 'subfolder'} to: ${editedName}`,
              severity: "success",
            });
            fetchTreeData();
          });
        } else {
          let errorMsg = `Failed to rename ${node.type === 'folder' ? 'folder' : 'subfolder'}`;
          if (response.status === 401) errorMsg = 'Unauthorized: Please log in again';
          else if (response.status === 403) errorMsg = `Forbidden: You lack permission to rename ${node.type === 'folder' ? 'folders' : 'subfolders'}`;
          else if (response.status >= 500) errorMsg = 'Server error: Please try again later';
          setSnackbar({
            open: true,
            message: errorMsg,
            severity: "error",
          });
          throw new Error(errorMsg);
        }
      })
      .catch((error) => {
        console.error('Error editing folder:', error);
        setSnackbar({
          open: true,
          message: 'Network error: Unable to connect to the server',
          severity: "error",
        });
      });
  };

  const handleDeleteClick = () => {
    if (myPermissions.can_delete_folder || myPermissions.is_admin) {
      setOpenDialog(true);
    } else {
      setSnackbar({
        open: true,
        message: `You do not have permission to delete ${node.type === 'folder' ? 'folders' : 'subfolders'}.`,
        severity: "warning",
      });
    }
  };

  const handleDelete = () => {
    if (!(myPermissions.can_delete_folder || myPermissions.is_admin)) {
      setSnackbar({
        open: true,
        message: `You do not have permission to delete ${node.type === 'folder' ? 'folders' : 'subfolders'}.`,
        severity: "warning",
      });
      setOpenDialog(false);
      return;
    }

    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem('authToken');
    const url = `${apiUrl}/delete-folder/${node.id}/`;

    fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.ok) {
          setSnackbar({
            open: true,
            message: `Successfully deleted ${node.type === 'folder' ? 'folder' : 'subfolder'}: ${node.name}`,
            severity: "success",
          });
          fetchTreeData();
        } else {
          let errorMsg = `Failed to delete ${node.type === 'folder' ? 'folder' : 'subfolder'}`;
          if (response.status === 401) errorMsg = 'Unauthorized: Please log in again';
          else if (response.status === 403) errorMsg = `Forbidden: You lack permission to delete ${node.type === 'folder' ? 'folders' : 'subfolders'}`;
          else if (response.status >= 500) errorMsg = 'Server error: Please try again later';
          setSnackbar({
            open: true,
            message: errorMsg,
            severity: "error",
          });
          throw new Error(errorMsg);
        }
      })
      .catch((error) => {
        console.error('Error deleting folder:', error);
        setSnackbar({
          open: true,
          message: 'Network error: Unable to connect to the server',
          severity: "error",
        });
      });

    setOpenDialog(false);
    setAnchorEl(null);
  };

 const handlePracticeFlashcards = (event) => {
  event.stopPropagation();
  
  // Clear sidebar highlighting
  if (setActivePage) {
    setActivePage("Folder View");
  }
  
  navigate(`/practiceflashcard/${node.id}`);
  handleMenuClose();
};

  const isSelected = node.id === selectedFolderId;

const handleNodeClick = () => {
  if (!(myPermissions.can_read_folder || myPermissions.is_admin)) {
    setSnackbar({
      open: true,
      message: `You do not have permission to view ${node.type === 'folder' ? 'folders' : 'subfolders'}.`,
      severity: "warning",
    });
    return;
  }
  
  onSelect(node.id, node.type);
  
  // Clear sidebar highlighting when clicking any folder
  if (setActivePage) {
    setActivePage("Folder View");
  }
  // Immediately update the page title to prevent flicker
if (window.updatePageTitle) {
  window.updatePageTitle(node);
}
  // Navigate based on folder type
  navigate(`/cards/${node.id}`, { 
    state: { 
      folderName: node.name, 
      folderType: node.type,
      parentId: node.parent || null 
    } 
  });
  
  // Toggle expanded state
  if (setExpandedFolders) {
    setExpandedFolders(prev => 
      prev.includes(node.id) 
        ? prev.filter(id => id !== node.id) 
        : [...prev, node.id]
    );
  }
};



  const highlightStyle = isSelected
    ? { backgroundColor: 'rgb(124, 182, 246)', borderRadius: '4px', color: 'blue' }
    : {};

  // Handle clicks outside the folder/subfolder creation form
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (creatingFolder && folderFormRef.current && !folderFormRef.current.contains(event.target)) {
        setCreatingFolder(false);
        setNewName('');
      }
      if (creatingSubfolder && subfolderFormRef.current && !subfolderFormRef.current.contains(event.target)) {
        setCreatingSubfolder(false);
        setNewName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [creatingFolder, creatingSubfolder]);

  return (
    <div ref={drop} style={{ opacity: isDragging ? 0.5 : 1, backgroundColor: 'transparent', padding:0, margin:0, marginLeft:"10px"  }} className={`tree-node ${isSelected ? 'selected' : ''}`}>
  <div
  ref={drag}
  className={hasChildren ? (isExpanded ? 'expanded' : 'collapsed') : ''}
  onClick={handleNodeClick}
  style={{
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: '4px',
    margin: '2px 0',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: isSelected
      ? mode === "dark"
        ? 'rgba(68, 166, 233, 0.2)'
        : mode === "custom"
        ? `${customColors.secondary}20`
        : 'rgba(20, 43, 225, 0.1)'
      : 'transparent',
    borderLeft: isSelected
      ? `4px solid ${mode === "dark" ? "#44a6e9" : mode === "custom" ? customColors.secondary : "#142be1"}`
      : '4px solid transparent',
  }}
  onMouseEnter={(e) => {
    if (!isSelected) {
      e.currentTarget.style.backgroundColor = mode === "dark"
        ? 'rgba(68, 166, 233, 0.05)'
        : mode === "custom"
        ? `${customColors.secondary}10`
        : 'rgba(20, 43, 225, 0.05)';
    }
  }}
  onMouseLeave={(e) => {
    if (!isSelected) {
      e.currentTarget.style.backgroundColor = 'transparent';
    }
  }}
>

        {hasChildren && (isExpanded ? <KeyboardArrowDownIcon /> : <ArrowForwardIosIcon style={{ fontSize: 'small' }} />)}
        <Box style={{ display: 'flex', alignItems: 'center', flexGrow: 1, overflow: 'hidden' }}>
          {isEditing ? (
            <form onSubmit={handleEditSubmit} style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                autoFocus
                margin="dense"
              />
              <IconButton type="submit" size="small">
                <AddIcon />
              </IconButton>
            </form>
          ) : (
            <>
             <Typography 
  noWrap 
  style={{ 
    flexGrow: 1, 
    display: 'flex', 
    alignItems: 'center',
    fontWeight: isSelected ? 600 : 400,
    // color: isSelected
    //   ? mode === "dark"
    //     ? "#44a6e9"
    //     : mode === "custom"
    //     ? customColors.secondary
    //     : "#142be1"
    //   : 'inherit'
  }}
>
               {node.type === 'folder' ? (
  <FolderIcon sx={{ color: mode === "dark" ? "#f9d66bff" : "#ffbf00ff", fontSize: 24, mr: 1 }} />
) : (
  <FolderOpen sx={{ color: mode === "dark" ? "#f9d66bff" : "#ffbf00ff", fontSize: 24, mr: 1 }} />
)}

                {/* <Tooltip title={node.name} arrow placement="left"> */}
                  <span>{node.name}</span>
                {/* </Tooltip> */}
              </Typography>
              {node.type !== 'subfolder' && (myPermissions.can_create_folder || myPermissions.is_admin) && (
                <IconButton onClick={handlePlusClick} size="small" sx={{'&:focus': { outline: 'none' }}}>
                  <AddIcon style={{ fontSize: 'medium' }} sx={{ color: mode === "dark" ? "#90caf9" : mode === "custom" ? customColors.text : "#1565c0" }} />
                </IconButton>
              )}
              <IconButton onClick={handleMenuClick} size="small" sx={{'&:focus': { outline: 'none' }}}>
                <MoreVertIcon style={{ fontSize: 'medium', '&:focus': { outline: 'none' } }} sx={{ color: mode === "dark" ? "#90caf9" : mode === "custom" ? customColors.text : "#1565c0" }} />
              </IconButton>
              <Menu anchorEl={plusAnchorEl} open={Boolean(plusAnchorEl)} onClose={handleMenuClose} 
               PaperProps={{
    elevation: 3,
    sx: {
      borderRadius: '8px',
      backgroundColor: mode === "dark" ? '#333' : '#fff',
    },
  }}>
                {/* <MenuItem
                  onClick={() => handleCreateClick('folder')}
                  disabled={!(myPermissions.can_create_folder || myPermissions.is_admin)}
                  sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.background : "#000" }}
                >
                  Create Folder
                </MenuItem> */}
                <MenuItem
                  onClick={() => handleCreateClick('subfolder')}
                  disabled={!(myPermissions.can_create_folder && myPermissions.can_create_sub) && !myPermissions.is_admin}
                  sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.background : "#000" }}
                >
                  Create Subfolder
                </MenuItem>
              </Menu>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} 
               PaperProps={{
    elevation: 3,
    sx: {
      borderRadius: '8px',
      backgroundColor: mode === "dark" ? '#333' : '#fff',
    },
  }}>
                {node.type === 'subfolder' && (
                  <MenuItem onClick={handlePracticeFlashcards} sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.background : "#000" }}>Practice Flashcards</MenuItem>
                )}
                <MenuItem
                  onClick={handleEdit}
                  disabled={!(myPermissions.can_update_folder || myPermissions.is_admin)}
                  sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.background : "#000" }}
                >
                  {node.type === 'folder' ? 'Rename Folder' : 'Rename Subfolder'}
                </MenuItem>
                <MenuItem
                  onClick={handleDeleteClick}
                  disabled={!(myPermissions.can_delete_folder || myPermissions.is_admin)}
                  sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.background : "#000" }}
                >
                  Delete
                </MenuItem>
              </Menu>
              <Dialog open={openDialog} onClose={handleCloseDialog} >
                <DialogTitle sx={{ color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.background : "#000" }}>Delete Confirmation</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to delete this folder? This action cannot be undone.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseDialog} color="primary">
                    Cancel
                  </Button>
                  <Button onClick={handleDelete} color="error">
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
      </div>
      {creatingFolder && (
        <form  ref={folderFormRef} onSubmit={(e) => handleCreateSubmit(e, 'folder')}>
          <TextField
            label="Enter folder name, press enter"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
            variant="outlined"
            size="small"
            autoFocus
            fullWidth
            margin="dense"
            InputLabelProps={{
    style: {
      color:
        mode === "dark"
          ? "#ffffff" // dark mode
          : mode === "custom"
          ? customColors.text // custom theme
          : "#000000", // light mode
    },
  }}
  sx={{
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor:
          mode === "dark"
            ? "#888"
            : mode === "custom"
            ? customColors.primary
            : "#ccc",
      },
      "&:hover fieldset": {
        borderColor:
          mode === "dark"
            ? "#bbb"
            : mode === "custom"
            ? customColors.primary
            : "#333",
      },
    },
    input: {
      color:
        mode === "dark"
          ? "#fff"
          : mode === "custom"
          ? customColors.text
          : "#000",
    },
  }}
          />
          <IconButton type="submit" size="small">
            <AddIcon />
          </IconButton>
        </form>
      )}
      {creatingSubfolder && (
        <form ref={subfolderFormRef} onSubmit={(e) => handleCreateSubmit(e, 'subfolder')}>
          <TextField
            label="Enter subfolder name, press enter"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
            variant="outlined"
            size="small"
            autoFocus
            fullWidth
            margin="dense"
            InputLabelProps={{
    style: {
      color:
        mode === "dark"
          ? "#ffffff" // dark mode
          : mode === "custom"
          ? customColors.text // custom theme
          : "#000000", // light mode
    },
  }}
  sx={{
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor:
          mode === "dark"
            ? "#888"
            : mode === "custom"
            ? customColors.primary
            : "#ccc",
      },
      "&:hover fieldset": {
        borderColor:
          mode === "dark"
            ? "#bbb"
            : mode === "custom"
            ? customColors.primary
            : "#333",
      },
    },
    input: {
      color:
        mode === "dark"
          ? "#fff"
          : mode === "custom"
          ? customColors.text
          : "#000",
    },
  }}
          />
          <IconButton type="submit" size="small">
            <AddIcon />
          </IconButton>
        </form>
      )}
   {hasChildren && (
  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
    <Box sx={{ pl: 2 }}> {/* Add this wrapper */}
      {node.children.map((childNode) => (
        <TreeNode
          key={childNode.id}
          node={childNode}
          moveNode={moveNode}
          onSelect={onSelect}
          selectedFolderId={selectedFolderId}
          fetchTreeData={fetchTreeData}
          myPermissions={myPermissions}
           setActivePage={setActivePage}
           expandedFolders={expandedFolders}
          setExpandedFolders={setExpandedFolders}
        />
      ))}
    </Box>
  </Collapse>
)}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={isDuplicateSubfolderAlertOpen} onClose={handleCloseDuplicateSubfolderAlert}>
        <DialogTitle>Duplicate Subfolder</DialogTitle>
        <DialogContent>
          <p>{duplicateSubfolderErrorMessage || 'A subfolder with this name already exists under the parent subfolder.'}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateSubfolderAlert} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// export default TreeNode;
// At the bottom of TreeNode.js
export default React.memo(TreeNode);
